import { useSelector } from "react-redux";
import { FC, ComponentProps } from "react";
import * as _ from "lodash";

import {
  ChartFilterType,
  DIMGroupList,
  Enterprise,
  EnterpriseAppState,
  HerdGroupEntity,
  HerdGroupList,
  PenEntity,
  PensList,
  DIMGroupEntity,
  DataDescriptorEntity,
  ChartFilters
} from "@ctra/api";

import { Analytics, FilterOutlined, FilterFilled, CheckboxMenu, Space } from "@ctra/components";
import { useTranslation, Enterprise as Content } from "@ctra/i18n";

import { DIMGroupFilter, HerdGroupFilter, PenFilter, useChartAPI, useChartFilters } from "@chart";
import { GACategories } from "@analytics";

const {
  V3: { MultiDropdown }
} = Analytics;

const {
  chart: { series: seriesAsset },
  analytics: {
    config: {
      metric: { filter }
    }
  }
} = Content;

/**
 * Filter controller for variant
 * @param {string} variantID
 * @param {string | undefined} type
 * @returns {JSX.Element | null}
 * @constructor
 */
export const Filter: FC<{ variantID: DataDescriptorEntity["id"]; type?: string }> = ({ variantID, type }) => {
  const { t } = useTranslation();

  const {
    filters,
    api: { getSeries, getMetadata, setFilter, setSeries }
  } = useChartFilters();

  const { charts } = useChartAPI();

  const {
    entity: {
      flags: { isSeriesTranslatable }
    }
  } = charts[0];

  const { supportedfarmIDs } = getMetadata(variantID);
  const { herdGroupID, dimGroupKey, penName } = _.get(filters, [variantID], {} as ChartFilters);
  const series = getSeries(variantID);

  /**
   * Handle herd group changes
   * @param herdGroupIDList
   */
  const onHerdGroupChange: ComponentProps<typeof HerdGroupFilter>["onHerdGroupChange"] = (herdGroupIDList) =>
    setFilter(variantID, ChartFilterType.herdGroup, herdGroupIDList);

  /**
   * Handle pen changes
   * @param {Array<PenEntity["id"]>} penNameList
   */
  const onPenChange: ComponentProps<typeof PenFilter>["onPenChange"] = (
    penNameList: Array<PenEntity["id"]>
  ) => setFilter(variantID, ChartFilterType.penName, penNameList);

  /**
   * Handle DIM group changes
   * @param {Array<DIMGroupEntity["id"]>} dimGroupIDList
   */
  const onDIMGroupChange: ComponentProps<typeof DIMGroupFilter>["onDIMGroupChange"] = (
    dimGroupIDList: Array<DIMGroupEntity["id"]>
  ) => setFilter(variantID, ChartFilterType.dimGroup, dimGroupIDList);

  /**
   * Fetch the DIM groups which are available for the given farms
   * @type {DIMGroupList}
   */
  const dimGroups = useSelector<EnterpriseAppState, DIMGroupList>((state) =>
    Enterprise.entities.getDIMGroups(state, { farmID: supportedfarmIDs })
  );

  /**
   * Fetch the herd groups which are available for the given farms
   */
  const herdGroups = useSelector<EnterpriseAppState, HerdGroupList>((state) =>
    Enterprise.entities.getHerdGroups(state, { farmID: supportedfarmIDs })
  );

  /**
   * Pick the pens which are available for the given farms
   * @type {PensList}
   */
  const pens = useSelector<EnterpriseAppState, PensList>((state) =>
    Enterprise.entities.getPens(state, { farmID: supportedfarmIDs })
  );

  /**
   * Combine the two lists
   * @type {{[p: string]: PenEntity, [p: number]: HerdGroupEntity}}
   */
  const input: Record<HerdGroupEntity["id"] | PenEntity["id"], HerdGroupEntity | PenEntity> = {
    ...pens,
    ...herdGroups
  };

  return (
    <Space size={8}>
      {!!dimGroupKey && (
        <MultiDropdown
          icon={FilterOutlined}
          values={_.map(_.sortBy(dimGroups, "sortableName"), "id")}
          selection={dimGroupKey}
          onChange={onDIMGroupChange}
          placeholder={t<string>(filter, { type: "dimGroup" })}
        />
      )}
      {!!herdGroupID && (
        <MultiDropdown<HerdGroupEntity["id"]>
          values={_.map(
            _.sortBy(herdGroups, (group) => {
              /**
               * some of the group names are numeric strings, we pad them so, they can be ordered lexicographically
               */
              return _.isInteger(_.parseInt(group.name)) ? _.padStart(group.name, 4) : group.name;
            }),
            "id"
          )}
          selection={herdGroupID}
          onChange={onHerdGroupChange}
          placeholder={t<string>(filter, { type: "herdGroupID" })}
        />
      )}
      {!!penName && (
        <MultiDropdown<PenEntity["id"]>
          values={_.uniq(
            _.map(
              _.sortBy(input, (entity) => parseFloat(entity.name)),
              "name"
            )
          )}
          selection={penName}
          onChange={onPenChange}
          placeholder={t<string>(filter, { type: "penName" })}
        />
      )}
      {/** Show the checkbox even if there is only one option available */}
      <CheckboxMenu
        disabled={_.size(series.available) === 0}
        icon={_.size(series.active) > 0 ? <FilterFilled /> : <FilterOutlined />}
        size="large"
        data-gtm-category={GACategories.configurator}
        data-gtm-action="Change filters"
        data-gtm-info={JSON.stringify({ values: series.active })}
        onChange={(value) =>
          setSeries(variantID, {
            available: series.available,
            active: value as string[]
          })
        }
        items={_.map(series.available, (label, value) => ({
          label: isSeriesTranslatable ? t<string>(seriesAsset(value)) : label,
          value
        }))}
        value={_.values(series.active)}
      >
        {t<string>(filter, { type })}
      </CheckboxMenu>
    </Space>
  );
};
